import type { PlanListPage, PlanSummary } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  CUSTOM_PLANS_TAB_ID_PREFIX,
  customPlansEmptyKind,
  EMPTY_CUSTOM_PLAN_COUNTS,
  focusPlan,
  type PlanBucketOrAll,
} from '../lib/custom-plans';
import { pastTheEndResetting, serverPageNav } from '../lib/paginate';
import { CardGridSkeleton } from './CardGridSkeleton';
import { CustomPlanRow } from './CustomPlanRow';
import { CustomPlansNotInListNotice } from './CustomPlansNotInListNotice';
import { onCustomPlanListKeyDown } from './custom-plans-keyboard';
import { EmptyState } from './EmptyState';
import { Pager } from './Pager';
import { PlanBreakdown } from './PlanBreakdown';
import type { QueryLike } from './QueryBoundary';
import { QueryBoundary } from './QueryBoundary';
import type { TabItem } from './TabList';
import { tabId, tabPanelId } from './TabList';

// The six tab panels — one per tab, named via
// `tabPanelId(CUSTOM_PLANS_TAB_ID_PREFIX, id)`. ALL SIX panel elements render
// on EVERY pass, regardless of the section's query state — loading, erroring,
// or resolved-empty. The query is filtered by bucket/repo/q, and
// `useCustomPlans`'s placeholderData (`lib/plan.ts`) is scoped to that SAME
// bucket/repo/q, so a tab, repo or search change genuinely re-enters loading
// for a beat; only a page change or the 30s poll keeps the previous rows
// painted. "loading" and "empty" are therefore real, common states here, not
// rare edge cases. Only the SELECTED panel's `<Show>` mounts real
// content; the other five stay empty stubs carrying `aria-labelledby`. That
// is what keeps every tab's `aria-controls` resolved to a real DOM element in
// every state — nesting the whole scaffold inside one `<QueryBoundary/>` (the
// shape this replaced) drops all six divs the instant that ONE query is
// loading or empty, which is exactly the axe failure (`aria-valid-attr-value`)
// this shape exists to avoid. axe does not catch a dangling `aria-controls`
// on an unselected tab by itself — `CustomPlansSection.test.tsx` walks each
// tab and asserts the id resolves explicitly.
//
// The selected panel owns its own `<QueryBoundary/>` over the SAME `query` —
// switching tabs unmounts one and mounts the next, which is fine: only one is
// ever mounted at a time, and the underlying query object is shared and
// already deduplicated by the query client.
export function CustomPlansPanels(props: {
  readonly items: readonly TabItem[];
  readonly status: PlanBucketOrAll;
  readonly query: QueryLike<PlanListPage>;
  readonly filters: { readonly repo: string | null; readonly q: string | null };
  readonly planParam: () => string | null;
  readonly setPlanParam: (next: string | null) => void;
  /**
   * The URL's OWN page number — not `data.page`. Under `placeholderData`,
   * `data.page` is whatever page the LAST-LANDED response
   * carried, which stays the OLD page until a page-change's own fetch
   * resolves; the pager would then show the reader's own click as not having
   * registered. The URL updates the instant the click writes it, so it is the
   * one thing both this pager and `routes/Plans.tsx`'s now read.
   */
  readonly page: () => number;
  readonly setPage: (page: number) => void;
  /** The section's search-input ref, forwarded to the `/` shortcut handler. */
  readonly searchInputRef: () => HTMLInputElement | undefined;
  readonly token: string | null;
}): JSX.Element {
  const emptyMessage = (): string =>
    customPlansEmptyKind(props.query.data?.counts ?? EMPTY_CUSTOM_PLAN_COUNTS, props.filters) ===
    'account_empty'
      ? t('fleet.plans.custom.empty')
      : t('fleet.plans.custom.no_matches');

  return (
    <For each={props.items}>
      {(item) => {
        // `data.plans.length === 0 && data.page <= 1`, not a bare
        // `total === 0`: this predicate assumes a past-the-end response
        // (`plans: []`, `data.page > 1`) always carries a real nonzero
        // `total`. That holds for the reset effect's own case
        // (`CustomPlansSection.tsx` requires `total > 0` before it ever
        // rewrites the URL), but NOT in general — the port echoes back
        // whatever page was asked for rather than clamping it, so a
        // genuinely empty bucket/filter opened on a stale `?custom_page=`
        // bookmark can itself answer `{ plans: [], page: N, total: 0 }` for
        // N > 1 (`routes/Plans.tsx`'s `isEmpty` gates on `data.total === 0`
        // directly for the identical shape on the "all plans" panel — see
        // its docblock). `data.page`, never `props.page()` (the URL):
        // `placeholderData` keeps the LAST-LANDED envelope on screen while a
        // page/tab/filter change is still in flight, so the envelope's own
        // page can lag the URL by exactly the window this predicate has to
        // not misread. A page PAST the end (`plans: []`, `total > 0`,
        // `data.page > 1`) does NOT match — that is the past-the-end
        // effect's job (`CustomPlansSection.tsx`), not "this tab/filter has
        // none".
        const isEmpty = (data: PlanListPage): boolean =>
          data.plans.length === 0 && data.page <= 1 && props.planParam() === null;

        // The past-the-end reset effect (`CustomPlansSection.tsx`) fires on
        // exactly `total > 0 && plans.length === 0 && (URL) page > 1`, and
        // rewrites the URL page to 1 — but the rewrite races its own
        // refetch, so this render can still see the STALE past-the-end
        // envelope (`pastTheEndResetting`, `lib/paginate.ts`) after the URL
        // has already moved.
        const resetting = (data: PlanListPage): boolean =>
          pastTheEndResetting(data) && props.planParam() === null;

        // Hoisted OUTSIDE `QueryBoundary`'s `children(data)` callback —
        // mirrors `routes/Plans.tsx`'s equivalent hoist (#5264 review, item
        // 3). That callback is a plain closure Solid re-invokes whenever
        // `props.query.data` changes reference, producing a brand-new
        // `<PanelBody/>`/`<Pager/>` DOM subtree every time and dropping a
        // just-activated numbered button's focus to `<body>` the instant new
        // data lands. Driven by a memo over `props.query.data` directly
        // instead, `<Pager/>`'s DOM persists across a page change, so a
        // click's focus is simply never removed from under it.
        const visibleData = (): PlanListPage | undefined => {
          const data = props.query.data;
          if (data === undefined || props.query.isError) return undefined;
          if (isEmpty(data) || resetting(data)) return undefined;
          return data;
        };

        return (
          <div
            id={tabPanelId(CUSTOM_PLANS_TAB_ID_PREFIX, item.id)}
            role="tabpanel"
            aria-labelledby={tabId(CUSTOM_PLANS_TAB_ID_PREFIX, item.id)}
            hidden={props.status !== item.id}
          >
            <Show when={props.status === item.id}>
              <QueryBoundary
                query={props.query}
                isEmpty={isEmpty}
                loading={<CardGridSkeleton listClass="custom-plans__list" />}
                empty={<EmptyState message={emptyMessage()} />}
              >
                {(data) => {
                  // `PanelBody`'s own `rows.length > 0` fallback does not
                  // know a reset is under way; left alone it paints "no
                  // matches" (or "no plans yet") for the one tick before the
                  // real page-1 answer lands. Render the loading skeleton
                  // instead — the same slot every other in-flight refetch
                  // already shows — for precisely the window the reset
                  // effect is guaranteed to correct.
                  if (resetting(data)) {
                    return <CardGridSkeleton listClass="custom-plans__list" />;
                  }
                  return (
                    <PanelBody
                      rows={data.plans}
                      emptyMessage={emptyMessage()}
                      planParam={props.planParam}
                      setPlanParam={props.setPlanParam}
                      searchInputRef={props.searchInputRef}
                      token={props.token}
                    />
                  );
                }}
              </QueryBoundary>
              {/* Hoisted out of `QueryBoundary`'s `children` — see
                  `visibleData` above. Never visible while the
                  loading/error/empty/resetting branches are the ones
                  actually on screen (`visibleData` folds in the same
                  `isEmpty`/`resetting` checks the row list above uses). */}
              <Show when={visibleData()}>
                {(data) => (
                  <Pager
                    pagination={serverPageNav({
                      page: props.page,
                      total: () => data().total,
                      pageSize: () => data().per_page,
                      goTo: props.setPage,
                    })}
                    label={t('fleet.plans.custom.title')}
                  />
                )}
              </Show>
            </Show>
          </div>
        );
      }}
    </For>
  );
}

function PanelBody(props: {
  readonly rows: readonly PlanSummary[];
  /** Rendered when `rows` is empty but `?plan=` still names a (not-in-list) id. */
  readonly emptyMessage: string;
  readonly planParam: () => string | null;
  readonly setPlanParam: (next: string | null) => void;
  readonly searchInputRef: () => HTMLInputElement | undefined;
  readonly token: string | null;
}): JSX.Element {
  let listWrapRef: HTMLDivElement | undefined;
  const isExpanded = (planId: string): boolean => {
    const f = focusPlan(props.rows, props.planParam());
    return f.kind === 'found' && f.plan.planId === planId;
  };
  const toggle = (planId: string): void => {
    props.setPlanParam(isExpanded(planId) ? null : planId);
  };
  const collapse = (): void => {
    if (props.planParam() !== null) props.setPlanParam(null);
  };
  return (
    <>
      {/* Outside the rows-present gate AND outside the `role="toolbar"` list
          below: a deep link onto an empty tab or an empty page must still
          show the notice and its full-view link, and the notice is its own
          `role="note"`, not part of the toolbar. */}
      <CustomPlansNotInListNotice rows={props.rows} planParam={props.planParam} />
      <Show when={props.rows.length > 0} fallback={<EmptyState message={props.emptyMessage} />}>
        <div
          class="custom-plans__list"
          ref={listWrapRef}
          role="toolbar"
          aria-label={t('fleet.plans.custom.title')}
          aria-orientation="vertical"
          onKeyDown={(event) =>
            onCustomPlanListKeyDown({
              event,
              listWrap: listWrapRef,
              searchInput: props.searchInputRef(),
              onCollapse: collapse,
              onToggle: toggle,
            })
          }
        >
          <For each={props.rows}>
            {(plan) => (
              <>
                <CustomPlanRow
                  plan={plan}
                  expanded={isExpanded(plan.planId)}
                  onToggle={() => toggle(plan.planId)}
                />
                <Show when={isExpanded(plan.planId)}>
                  <PlanBreakdown planId={plan.planId} token={props.token} />
                </Show>
              </>
            )}
          </For>
        </div>
      </Show>
    </>
  );
}
